import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axios";
import DOMPurify from "dompurify";
import { usePageHeaderByKey } from "../hooks/usePageHeaderBykey";
import { TbMoodEmpty } from "react-icons/tb";
import { truncateText, truncateHtml } from "../utils/authHelpers";
import PublicPageShell from "../components/PublicPageShell";
// Skeleton Loader
const BlogSkeleton = () => (
  <motion.article
    className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xs"
    whileHover={{ scale: 1.02 }}
  >
    <div className="h-56 w-full bg-gray-200 animate-pulse" />
    <div className="p-4 sm:p-6">
      <div className="h-4 w-1/3 bg-gray-200 rounded-full mb-2 animate-pulse" />
      <div className="h-4 w-1/2 bg-gray-200 rounded-full mb-2 animate-pulse" />
      <div className="h-4 w-1/4 bg-gray-200 rounded-full mb-2 animate-pulse" />
      <div className="h-6 w-3/4 bg-gray-200 rounded mb-3 animate-pulse" />
      <div className="h-4 w-full bg-gray-200 rounded mb-2 animate-pulse" />
      <div className="h-4 w-2/3 bg-gray-200 rounded mb-2 animate-pulse" />
      <div className="h-8 w-24 bg-gray-200 rounded mt-4 animate-pulse" />
    </div>
  </motion.article>
);

const BlogList = () => {
  const { pageHeader, isLoading } = usePageHeaderByKey("blog");
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axiosInstance
      .get("/api/blogs")
      .then((response) => setBlogs(response.data))
      .catch((error) => console.error("Error fetching blogs:", error))
      .finally(() => setLoading(false));
  }, []);

  const openBlog = (slug) => {
    navigate(`/blog/${slug}`);
  };

  return (
    <PublicPageShell>
      <div className="flex flex-col justify-center w-full">
        {/* Title Animation */}
        <motion.h1
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-2xl mb-4 text-center font-bold text-black uppercase"
        >
          {blogs.length === 0 && !loading && !isLoading
            ? "No News Yet"
            : pageHeader?.h1 || "Latest News"}
        </motion.h1>

        {/* Blogs Grid or Empty State */}
        {loading || isLoading ? (
          <motion.div
            className="grid mt-2 gap-6 sm:gap-8 md:gap-10 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full max-w-6xl"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.3 },
              },
            }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <BlogSkeleton key={i} />
            ))}
          </motion.div>
        ) : blogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center w-full py-16">
            <span className="text-6xl mb-4 text-gray-300">
              <TbMoodEmpty />
            </span>
            <h2 className="text-lg font-semibold text-gray-600 mb-2">
              No blog posts yet
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4 md:text-sm">
              Please check back later for updates and news!
            </p>
          </div>
        ) : (
          <motion.div
            className="grid mt-2 gap-6 sm:gap-8 md:gap-10 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.3 },
              },
            }}
          >
            {blogs.map((blog, index) => (
              <motion.article
                key={index}
                className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-md hover:shadow-lg transition-shadow flex flex-col min-h-[420px] cursor-pointer"
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
                }}
                whileHover={{ scale: 1.02 }}
                onClick={() => openBlog(blog.slug)}
              >
                {/* Blog Image or Video */}
                <div className="w-full h-56 bg-gray-200 overflow-hidden flex items-center justify-center" onClick={() => openBlog(blog.slug)}>
                  {blog.videos?.length > 0 ? (
                    <div className="relative">
                      <video
                        src={blog.videos[0].video_url || blog.videos[0].url}
                        className="w-full h-full object-cover"
                        muted
                        autoPlay
                        loop
                        playsInline
                        poster={blog.images?.[0]?.image_url}
                      />
                    </div>
                  ) : (
                    <img
                      alt={blog.title}
                      src={
                        blog.images?.[0]?.image_url ||
                        "https://placehold.co/400"
                      }
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                </div>

                <div className="p-3 sm:p-4 flex flex-col flex-1">
                  <div className="flex flex-wrap items-center mb-2">
                    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs m-1 whitespace-nowrap text-gray-600">
                      {new Date(blog.createdAt).toLocaleDateString()}
                    </span>
                    {blog.category && (
                      <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs m-1 whitespace-nowrap text-purple-600">
                        {blog.category.name}
                      </span>
                    )}
                    {blog.labels.length > 0 && (
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs m-1 whitespace-nowrap text-green-700">
                        {blog.labels
                          .map((label) => label.label.name)
                          .join(", ")}
                      </span>
                    )}
                  </div>
                  <span
                    onClick={() => openBlog(blog.slug)}
                    className="mt-2 block hover:underline"
                  >
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-2">
                      {truncateText(blog.title, 50)}
                    </h3>
                  </span>
                  <div
                    className="mt-2 line-clamp-3 text-gray-500"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(truncateHtml(blog.content, 150)),
                    }}
                  />
                  <button
                    onClick={() => openBlog(blog.slug)}
                    className="group mt-auto inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    Read More
                    <span
                      aria-hidden="true"
                      className="block transition-all group-hover:ms-0.5"
                    >
                      &rarr;
                    </span>
                  </button>
                </div>
              </motion.article>
            ))}
          </motion.div>
        )}
      </div>
    </PublicPageShell>
  );
};

export default BlogList;
